import { MutableRefObject, RefObject, useEffect, useMemo, useRef } from 'react'
import { useGridBlock, usePage } from '../../../contexts'
import { useAdBlockStore } from '../../../store'
import { IStory, IStoryCard } from '../../../types/stories'
import {
  IViewerAdSlot,
  IViewerPoint,
  ViewerCloseMethod,
  ViewerEvent,
} from '../../VerticalViewer/types'
import { fullAdUnit } from './storiesAds'
import {
  baseParams,
  cardParams,
  episodeLabel,
  IStoriesContext,
  milestonesReached,
  sendStoriesEvent,
  StoriesEntry,
  storyParams,
} from './storiesAnalytics'
import { IStoriesState, storyStatus } from './storiesState'

type Params = Record<string, string | number>
type CloseMethod = ViewerCloseMethod | 'page_exit' | 'browser_back'
export type ShareMethod = 'copy_link' | 'web_share_api'
export type ShareResult = 'yes' | 'no' | 'unknown'

/** a time this far into a clip at its first report is a resume, not a start */
const RESUME_REPORT_S = 1
/** phase 1 has video ads only (the overlay is phase 2) */
const AD_TYPE = 'video'
/** the rail counts as seen when this much of it is on screen */
const RAIL_SEEN_RATIO = 0.5
/**
 * The rail seen waits this long at most for the ad-block check: a blocker is
 * known only after its retries (about 3 s), and the rail is often seen first
 */
const RAIL_ADBLOCK_WAIT_MS = 4000

interface ILive {
  block: { gridId: string | number | undefined; index?: number }
  adDecision: string | null
  gridId: number
}

const contextOf = (
  { block, adDecision, gridId }: ILive,
  entry: StoriesEntry | ''
): IStoriesContext => ({
  gridId,
  // a page without the block's own context gives the default one
  position:
    block.gridId === gridId && typeof block.index === 'number'
      ? block.index + 1
      : '',
  hasAdblock: adDecision === null ? '' : adDecision === 'block' ? 'yes' : 'no',
  entry,
})

/** one opening of the viewer, from open to close */
interface ISession {
  entry: StoriesEntry
  /** the rail as it stood at the opening: the viewer's groups */
  order: IStory[]
  at: number
  group: number
  clip: number
  time: number
  autoplay: boolean
  started: boolean
  /** the card play the progress events belong to */
  play: { key: string; sent: Set<number>; first: boolean } | null
  cards: Set<string>
  completed: Set<string>
  stories: Set<string>
  /** ads that showed (their impression) */
  ads: number
  /** taps on "next" per story, before the clip's end */
  skipped: Record<string, number>
  /** the ad-block wall showed in this opening */
  wall: boolean
}

interface IOptions {
  stories: IStory[]
  /** the rail on screen */
  ordered: IStory[]
  gridId: number
  stateRef: MutableRefObject<IStoriesState>
  sectionRef: RefObject<HTMLElement>
}

/**
 * The Stories block's GA4 events (D39). Everything is read from refs when it
 * happens, so the handlers never change and never read a stale value.
 */
export const useStoriesAnalytics = ({
  stories,
  ordered,
  gridId,
  stateRef,
  sectionRef,
}: IOptions) => {
  const pageData = usePage()
  const block = useGridBlock()
  const adDecision = useAdBlockStore(s => s.decision)
  const live = useRef({ pageData, block, adDecision, stories, ordered, gridId })
  live.current = { pageData, block, adDecision, stories, ordered, gridId }
  const session = useRef<ISession | null>(null)
  /** the rail seen, while it waits for the ad-block answer */
  const railPending = useRef<(() => void) | null>(null)

  const api = useMemo(() => {
    const fire = (
      event: string,
      extra: Params,
      story?: IStory,
      card?: IStoryCard,
      withCard = false,
      entry?: StoriesEntry
    ) => {
      // the rail was seen before anything the user does in it
      railPending.current?.()
      const { stories: all, ordered: rail, pageData: page } = live.current
      const position = story ? rail.findIndex(s => s.id === story.id) + 1 : 0
      sendStoriesEvent(page, event, {
        ...baseParams(
          contextOf(live.current, entry ?? session.current?.entry ?? ''),
          story,
          card
        ),
        ...(story
          ? storyParams(
              story,
              position,
              storyStatus(story, all, stateRef.current)
            )
          : {}),
        ...(card && withCard
          ? cardParams(card, session.current?.autoplay ?? true)
          : {}),
        ...extra,
      })
    }

    /** the story and card the session is on */
    const current = () => {
      const s = session.current
      const story = s?.order[s.group]
      return { s, story, card: story?.cards[s?.clip ?? 0] }
    }

    const cardEvent = (event: string, extra: Params = {}) => {
      const { s, story, card } = current()
      if (s && story && card) {
        fire(
          event,
          { card_position_sec: Math.round(s.time), ...extra },
          story,
          card,
          true
        )
      }
    }

    /** an ad's event: Base, story and card of the content card before it */
    const adEvent = (event: string, ad: IViewerAdSlot, extra: Params) => {
      const story = session.current?.order[ad.group]
      const card = story?.cards[ad.clip]
      if (story && card) {
        fire(event, extra, story, card, true)
      }
    }

    const close = (method: CloseMethod) => {
      const { s, story, card } = current()
      if (!s) {
        return
      }
      session.current = null
      fire(
        'player_close',
        {
          close_method: method,
          card_position_sec: Math.round(s.time),
          cards_watched: s.cards.size,
          cards_completed: s.completed.size,
          stories_watched: s.stories.size,
          ads_shown: s.ads,
          session_duration_sec: Math.round((Date.now() - s.at) / 1000),
        },
        story,
        card,
        true,
        s.entry
      )
    }

    return {
      bubbleClick: (story: IStory) =>
        fire('bubble_click', {}, story, undefined, false, 'stories_rail'),

      open: (
        order: IStory[],
        group: number,
        point: IViewerPoint,
        entry: StoriesEntry
      ) => {
        close('x')
        session.current = {
          entry,
          order,
          at: Date.now(),
          group,
          clip: point.clip,
          time: point.time,
          // only the first clip of an opening by a tap is not autoplay
          autoplay: entry !== 'stories_rail',
          started: false,
          play: null,
          cards: new Set(),
          completed: new Set(),
          stories: new Set(),
          ads: 0,
          skipped: {},
          wall: false,
        }
        const story = order[group]
        if (story) {
          fire(
            'player_open',
            // a link opens on its own card: not a resume
            {
              is_resume:
                entry !== 'deeplink' && (point.clip > 0 || point.time > 0)
                  ? 'yes'
                  : 'no',
            },
            story,
            story.cards[point.clip]
          )
        }
      },

      /** a link no block holds has no story or card */
      deeplink: (
        status: 'success' | 'card_not_found',
        story?: IStory,
        card?: IStoryCard
      ) =>
        fire(
          'deeplink_open',
          { deeplink_status: status },
          story,
          card,
          true,
          'deeplink'
        ),

      clipStart: (group: number, clip: number) => {
        const s = session.current
        if (s) {
          s.group = group
          s.clip = clip
          s.time = 0
        }
      },

      time: (group: number, clip: number, time: number) => {
        const s = session.current
        const card = s?.order[group]?.cards[clip]
        if (!s || !card || s.group !== group || s.clip !== clip) {
          return
        }
        s.time = time
        const play = s.play
        if (!play || play.key !== `${group}-${clip}`) {
          return
        }
        if (play.first) {
          play.first = false
          // resumed mid-clip: the milestones before the resume point are not this play's
          if (time > RESUME_REPORT_S) {
            milestonesReached(time, card.duration, play.sent).forEach(m =>
              play.sent.add(m)
            )
            return
          }
        }
        milestonesReached(time, card.duration, play.sent).forEach(m => {
          play.sent.add(m)
          cardEvent(`card_reach_${m}`)
        })
      },

      event: (e: ViewerEvent) => {
        const s = session.current
        if (!s) {
          return
        }
        const story = s.order[s.group]
        switch (e.type) {
          case 'clipPlaying': {
            const playing = s.order[e.group]
            const card = playing?.cards[e.clip]
            if (!playing || !card) {
              return
            }
            s.group = e.group
            s.clip = e.clip
            s.autoplay = s.started || s.entry !== 'stories_rail'
            s.started = true
            s.play = {
              key: `${e.group}-${e.clip}`,
              sent: new Set(),
              first: true,
            }
            s.cards.add(`${playing.id}-${card.order}`)
            s.stories.add(playing.id)
            cardEvent('card_start', { start_trigger: e.trigger })
            return
          }
          case 'clipEnd': {
            const ended = s.order[e.group]
            const card = ended?.cards[e.clip]
            if (!ended || !card || e.group !== s.group || e.clip !== s.clip) {
              return
            }
            s.play = null
            // a card played out twice is one card completed
            s.completed.add(`${ended.id}-${card.order}`)
            cardEvent('card_reach_100')
            if (e.clip === ended.cards.length - 1) {
              const { stories: all } = live.current
              // an older episode of the rail still not watched
              const older = all.slice(all.findIndex(x => x.id === ended.id) + 1)
              const nonLinear = older.some(
                x => storyStatus(x, all, stateRef.current) !== 'watched'
              )
              fire(
                'story_complete',
                {
                  watch_order: nonLinear ? 'non_linear' : 'linear',
                  cards_skipped: s.skipped[ended.id] ?? 0,
                },
                ended,
                card
              )
            }
            return
          }
          case 'nav':
            if (e.direction === 'next' && story) {
              s.skipped[story.id] = (s.skipped[story.id] ?? 0) + 1
            }
            cardEvent('card_nav', { direction: e.direction })
            return
          case 'groupNav':
            // the last card played out: the next story came by itself
            if (story && e.via === 'auto') {
              const nextStory = s.order[e.to]
              fire(
                'story_auto_next',
                {
                  next_story_id: nextStory?.id ?? '',
                  next_story_episode_number: nextStory
                    ? episodeLabel(nextStory)
                    : '',
                },
                story
              )
              return
            }
            if (story) {
              fire(
                'story_nav_click',
                {
                  nav_type: e.via,
                  direction: e.direction,
                  target_story_id: s.order[e.to]?.id ?? '',
                },
                story,
                story.cards[s.clip]
              )
            }
            return
          case 'pause':
            cardEvent('pause', { pause_method: e.via })
            return
          case 'error':
            cardEvent('card_error', {
              error_type: e.errorType,
              error_message: e.message,
            })
            return
          case 'adRequest':
            adEvent('ad_request', e.ad, {
              ad_type: AD_TYPE,
              ad_unit: e.ad.adUnit ? fullAdUnit(e.ad.adUnit) : '',
              ad_slot_index: e.ad.slot,
            })
            return
          case 'adImpression':
            s.ads += 1
            adEvent('ad_impression', e.ad, {
              ad_type: AD_TYPE,
              ad_unit: e.ad.adUnit ? fullAdUnit(e.ad.adUnit) : '',
              ad_slot_index: e.ad.slot,
              creative_id: e.creative.creativeId,
              line_item_id: e.creative.lineItemId,
              ad_duration: Math.round(e.creative.duration),
              ad_orientation: e.creative.orientation,
              ad_start_muted: e.startMuted ? 'yes' : 'no',
              // muted by the user (a choice that holds between visits) or
              // by the browser (no sound without a tap)
              mute_reason: !e.startMuted
                ? ''
                : stateRef.current.muted
                ? 'user'
                : 'browser',
            })
            return
          case 'adComplete':
            adEvent('ad_complete', e.ad, {
              ad_slot_index: e.ad.slot,
              creative_id: e.creative.creativeId,
              ad_duration: Math.round(e.creative.duration),
            })
            return
          case 'adSkip':
            adEvent('ad_skip', e.ad, {
              ad_slot_index: e.ad.slot,
              creative_id: e.creative?.creativeId ?? '',
              time_in_ad_sec: Math.round(e.time),
            })
            return
          case 'adClick':
            adEvent('ad_click', e.ad, {
              ad_type: AD_TYPE,
              ad_slot_index: e.ad.slot,
              creative_id: e.creative?.creativeId ?? '',
            })
            return
          case 'adError':
            adEvent('ad_error', e.ad, {
              ad_type: AD_TYPE,
              ad_unit: e.ad.adUnit ? fullAdUnit(e.ad.adUnit) : '',
              ad_slot_index: e.ad.slot,
              error_type: e.errorType,
              error_code: e.errorCode,
            })
            return
          case 'close':
            close(e.method)
            return
          default:
        }
      },

      adblockWall: () => {
        const { s, story, card } = current()
        if (s && story && card && !s.wall) {
          s.wall = true
          fire('adblock_wall_view', {}, story, card, true)
        }
      },

      mute: (muted: boolean) =>
        cardEvent('mute_toggle', { mute_state: muted ? 'muted' : 'unmuted' }),

      share: (
        story: IStory,
        cardIndex: number,
        method: ShareMethod,
        url: string,
        result: ShareResult
      ) => {
        const card = story.cards[cardIndex]
        if (card) {
          fire(
            'share_click',
            {
              share_method: method,
              share_url: url,
              share_completed: result,
            },
            story,
            card,
            true
          )
        }
      },

      close,
    }
  }, [stateRef])

  // the rail seen: once per page load. It waits a little for the ad-block
  // answer, but always goes, and before any other Stories event
  useEffect(() => {
    const section = sectionRef.current
    if (
      !section ||
      stories.length === 0 ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let unsubscribe: (() => void) | undefined
    const stopWaiting = () => {
      clearTimeout(timer)
      unsubscribe?.()
      railPending.current = null
    }
    const observer = new IntersectionObserver(
      entries => {
        if (
          !entries.some(entry => entry.intersectionRatio >= RAIL_SEEN_RATIO)
        ) {
          return
        }
        observer.disconnect()
        // the rail as it was seen: a card played while waiting does not count
        const { stories: all, pageData } = live.current
        const watched = all.filter(
          story => storyStatus(story, all, stateRef.current) === 'watched'
        ).length
        // and the page it was seen on: a client move changes the address
        // before the block goes, and sendGA4 would read the next page's
        const { href, origin, pathname } = window.location
        const send = () => {
          stopWaiting()
          // read from the store: the answer may be newer than the last render
          const adDecision = useAdBlockStore.getState().decision
          sendStoriesEvent(pageData, 'rail_impression', {
            ...baseParams(contextOf({ ...live.current, adDecision }, '')),
            page_url: href,
            clean_page_url: origin + pathname,
            bubbles_count: all.length,
            unwatched_count: all.length - watched,
            watched_count: watched,
          })
        }
        if (useAdBlockStore.getState().decision !== null) {
          send()
          return
        }
        railPending.current = send
        unsubscribe = useAdBlockStore.subscribe(({ decision }) => {
          if (decision !== null) {
            send()
          }
        })
        timer = setTimeout(send, RAIL_ADBLOCK_WAIT_MS)
      },
      { threshold: RAIL_SEEN_RATIO }
    )
    observer.observe(section)
    return () => {
      observer.disconnect()
      // the page left while waiting: sent with what is known
      railPending.current?.()
    }
    // once per page: the stories are read when it is seen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stories.length > 0])

  // the tab closed, or the page left (a client move unmounts the block)
  useEffect(() => {
    const onHide = () => {
      railPending.current?.()
      api.close('page_exit')
    }
    window.addEventListener('pagehide', onHide)
    return () => {
      window.removeEventListener('pagehide', onHide)
      api.close('page_exit')
    }
  }, [api])

  return api
}
